import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  InvoiceSubmission,
  TimeCorrectionRequest,
  WorkHolidayItem,
} from '../types';
import { formatTimeclockEntryKind } from '../utils/timeclockEntryKind';
import StatusChip, { StatusTone } from './ui/StatusChip';

export type HolidayCalendarVariant = 'hero' | 'surface';

export interface HolidayCalendarProps {
  variant?: HolidayCalendarVariant;
  className?: string;
  /** Layout reduzido com painel lateral de informações do dia. */
  compact?: boolean;
  /** Carrega pedidos de NF e correção de hora (ponto). */
  showTimeclockRequests?: boolean;
  /**
   * Uso como seletor de data controlado (YYYY-MM-DD). Quando definido, o dia
   * selecionado passa a ser controlado pelo pai e `onSelectDate` é chamado a
   * cada escolha.
   */
  selectedDate?: string;
  onSelectDate?: (workDate: string) => void;
  /** Renderiza apenas o grid do calendário (sem painel lateral / legenda / lista). */
  pickerOnly?: boolean;
}

const WEEKDAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const MONTH_NAMES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const statusCorrectionPt: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
};

const statusInvoicePt: Record<string, string> = {
  pending: 'Pendente',
  under_review: 'Em análise',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
};

function workDateToLocalDate(workDate: string): Date {
  const [y, m, d] = workDate.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function dateToWorkDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeDateKey(value: string): string {
  return value.slice(0, 10);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function scopeLabel(scope: WorkHolidayItem['scope']): string {
  switch (scope) {
    case 'national':
      return 'Nacional';
    case 'state':
      return 'Estadual (BA)';
    case 'municipal':
      return 'Municipal (Ilhéus)';
    default:
      return scope;
  }
}

function formatDayLong(d: Date): string {
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatDayShort(d: Date): string {
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function statusTone(status: string): 'ok' | 'warn' | 'danger' {
  if (status === 'approved') return 'ok';
  if (status === 'rejected') return 'danger';
  return 'warn';
}

/** Map the internal status tone onto the shared StatusChip tone vocabulary. */
function statusChipTone(status: string): StatusTone {
  const tone = statusTone(status);
  if (tone === 'ok') return 'success';
  if (tone === 'danger') return 'danger';
  return 'warning';
}

function buildMonthGrid(year: number, monthIndex: number): Date[] {
  const first = new Date(year, monthIndex, 1);
  const startOffset = first.getDay();
  const gridStart = new Date(year, monthIndex, 1 - startOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
}

function dayTypeLabel(selected: Date, holiday?: WorkHolidayItem): { title: string; detail?: string } {
  if (holiday) {
    return { title: 'Feriado', detail: holiday.name };
  }
  const dow = selected.getDay();
  if (dow === 0 || dow === 6) {
    return { title: 'Fim de semana', detail: 'Horas contam como extra no ponto' };
  }
  return { title: 'Dia útil', detail: 'Expediente normal' };
}

const KICKER_SX = {
  display: 'block',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

const HolidayCalendar: React.FC<HolidayCalendarProps> = ({
  variant = 'hero',
  className = '',
  compact = false,
  showTimeclockRequests = false,
  selectedDate,
  onSelectDate,
  pickerOnly = false,
}) => {
  const { user } = useContext(AuthContext) || {};
  const today = useMemo(() => new Date(), []);
  const controlledSelected = useMemo(
    () => (selectedDate ? workDateToLocalDate(selectedDate.slice(0, 10)) : null),
    [selectedDate],
  );
  const [displayMonth, setDisplayMonth] = useState(() =>
    controlledSelected
      ? new Date(controlledSelected.getFullYear(), controlledSelected.getMonth(), 1)
      : new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [internalSelected, setInternalSelected] = useState<Date>(() => new Date(today));
  const selected = controlledSelected ?? internalSelected;

  /** Selects a day, honoring controlled mode. */
  const chooseDay = useCallback(
    (day: Date): void => {
      if (onSelectDate) onSelectDate(dateToWorkDateKey(day));
      if (!controlledSelected) setInternalSelected(day);
    },
    [onSelectDate, controlledSelected],
  );

  // Keep the visible month in sync when the controlled date jumps to another month.
  useEffect(() => {
    if (!controlledSelected) return;
    setDisplayMonth((prev) =>
      prev.getFullYear() === controlledSelected.getFullYear() &&
      prev.getMonth() === controlledSelected.getMonth()
        ? prev
        : new Date(controlledSelected.getFullYear(), controlledSelected.getMonth(), 1),
    );
  }, [controlledSelected]);
  const [holidays, setHolidays] = useState<WorkHolidayItem[]>([]);
  const [corrections, setCorrections] = useState<TimeCorrectionRequest[]>([]);
  const [invoices, setInvoices] = useState<InvoiceSubmission[]>([]);
  const [loading, setLoading] = useState(false);

  const year = displayMonth.getFullYear();
  const month = displayMonth.getMonth() + 1;
  const monthIndex = displayMonth.getMonth();

  const loadHolidays = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await api.get<WorkHolidayItem[]>(`/timeclock/holidays?year=${year}&month=${month}`);
      setHolidays(res.data);
    } catch {
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const loadTimeclockRequests = useCallback(async (): Promise<void> => {
    if (!showTimeclockRequests || !user) {
      setCorrections([]);
      setInvoices([]);
      return;
    }
    try {
      const [c, inv] = await Promise.all([
        api.get<TimeCorrectionRequest[]>('/timeclock/my/time-corrections'),
        api.get<InvoiceSubmission[]>('/timeclock/my/invoices'),
      ]);
      setCorrections(c.data);
      setInvoices(inv.data);
    } catch {
      setCorrections([]);
      setInvoices([]);
    }
  }, [showTimeclockRequests, user]);

  useEffect(() => {
    void loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    void loadTimeclockRequests();
  }, [loadTimeclockRequests]);

  const holidayByDate = useMemo(() => {
    const map = new Map<string, WorkHolidayItem>();
    for (const h of holidays) {
      map.set(h.workDate.slice(0, 10), h);
    }
    return map;
  }, [holidays]);

  const gridDays = useMemo(() => buildMonthGrid(year, monthIndex), [year, monthIndex]);

  const selectedKey = dateToWorkDateKey(selected);
  const selectedHoliday = holidayByDate.get(selectedKey);
  const dayType = dayTypeLabel(selected, selectedHoliday);

  const correctionsOnDay = useMemo(
    () => corrections.filter((c) => normalizeDateKey(c.workDate) === selectedKey),
    [corrections, selectedKey],
  );

  const invoicesOnDay = useMemo(
    () => invoices.filter((inv) => inv.issueDate && normalizeDateKey(inv.issueDate) === selectedKey),
    [invoices, selectedKey],
  );

  const hasDayTimeclockActivity = correctionsOnDay.length > 0 || invoicesOnDay.length > 0;
  const showAsideDayCard =
    !showTimeclockRequests || hasDayTimeclockActivity || Boolean(selectedHoliday);

  const goPrevMonth = (): void => {
    setDisplayMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goNextMonth = (): void => {
    setDisplayMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const dayCellSize = compact ? 30 : 40;

  const pickerBlock = (
    <Box>
      {/* Caption / month navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <IconButton size="small" onClick={goPrevMonth} aria-label="Mês anterior">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: compact ? '0.8rem' : '0.9rem',
            textTransform: 'capitalize',
          }}
        >
          {MONTH_NAMES[monthIndex]} {year}
        </Typography>
        <IconButton size="small" onClick={goNextMonth} aria-label="Próximo mês">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Weekday header row */}
      <Box
        role="grid"
        aria-label={`Calendário ${MONTH_NAMES[monthIndex]} ${year}`}
        sx={{ opacity: loading ? 0.85 : 1 }}
      >
        <Box
          role="row"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
            mb: 0.5,
          }}
        >
          {WEEKDAY_LABELS.map((label, idx) => (
            <Box
              key={`${label}-${idx}`}
              role="columnheader"
              sx={{
                textAlign: 'center',
                fontSize: compact ? '0.6rem' : '0.72rem',
                fontWeight: 500,
                color: 'text.secondary',
                textTransform: 'uppercase',
                py: 0.5,
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        {/* Weeks */}
        {Array.from({ length: 6 }, (_, weekIdx) => (
          <Box
            key={weekIdx}
            role="row"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5,
              mb: 0.5,
            }}
          >
            {gridDays.slice(weekIdx * 7, weekIdx * 7 + 7).map((day) => {
              const key = dateToWorkDateKey(day);
              const inMonth = day.getMonth() === monthIndex;
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const isHoliday = holidayByDate.has(key);
              const isSelected = isSameDay(day, selected);
              const isToday = isSameDay(day, today);
              const hasRequest =
                showTimeclockRequests &&
                (corrections.some((c) => normalizeDateKey(c.workDate) === key) ||
                  invoices.some((inv) => normalizeDateKey(inv.issueDate) === key));

              const daySx: Record<string, unknown> = {
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / 1',
                maxWidth: dayCellSize,
                minWidth: 0,
                mx: 'auto',
                p: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: compact ? '0.7rem' : '0.82rem',
                fontWeight: 400,
                lineHeight: 1,
                color: 'text.primary',
                bgcolor: 'transparent',
                minHeight: 0,
                '&:hover': { bgcolor: 'action.hover' },
              };

              if (!inMonth) {
                daySx.color = 'text.secondary';
                daySx.opacity = 0.45;
              }
              if (isWeekend && !isHoliday && !isSelected) {
                daySx.color = 'text.secondary';
              }
              if (isToday && !isSelected) {
                daySx.bgcolor = 'action.selected';
                daySx.fontWeight = 600;
              }
              if (isHoliday && !isSelected) {
                daySx.bgcolor = 'action.hover';
                daySx.color = 'text.primary';
                daySx.fontWeight = 600;
                daySx.boxShadow = (theme: { palette: { divider: string } }) =>
                  `inset 0 0 0 1px ${theme.palette.divider}`;
              }
              if (isSelected) {
                daySx.bgcolor = 'primary.main';
                daySx.color = 'primary.contrastText';
                daySx.fontWeight = 600;
                daySx['&:hover'] = { bgcolor: 'primary.dark' };
              }

              return (
                <Box
                  key={key}
                  role="gridcell"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Button
                    disableRipple
                    onClick={() => {
                      chooseDay(day);
                      if (!inMonth) {
                        setDisplayMonth(new Date(day.getFullYear(), day.getMonth(), 1));
                      }
                    }}
                    aria-pressed={isSelected}
                    aria-label={formatDayLong(day)}
                    sx={daySx}
                  >
                    {day.getDate()}
                    {hasRequest && !isSelected ? (
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          bottom: 3,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 4,
                          height: 4,
                          borderRadius: '999px',
                          bgcolor: 'primary.main',
                        }}
                      />
                    ) : null}
                  </Button>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const asideBlock = compact ? (
    <Box
      component="aside"
      aria-live="polite"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100%',
      }}
    >
      <Box component="header" sx={{ mb: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.25,
          }}
        >
          <Typography component="span" sx={KICKER_SX}>
            Notificações
          </Typography>
          {showTimeclockRequests && correctionsOnDay.length + invoicesOnDay.length > 0 ? (
            <StatusChip
              tone="accent"
              label={correctionsOnDay.length + invoicesOnDay.length}
            />
          ) : null}
        </Box>
        <Typography
          sx={{
            m: 0,
            mt: 0.5,
            fontSize: '0.92rem',
            fontWeight: 650,
            lineHeight: 1.35,
            textTransform: 'capitalize',
          }}
        >
          {formatDayLong(selected)}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pr: 0.5,
        }}
      >
        {showAsideDayCard ? (
          <Paper
            component="article"
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              p: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: selectedHoliday ? 'action.hover' : 'background.paper',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="strong"
                sx={{ display: 'block', mb: 0.5, fontSize: '0.92rem', fontWeight: 650 }}
              >
                {dayType.title}
              </Typography>
              {dayType.detail ? (
                <Typography sx={{ m: 0, fontSize: '0.82rem', lineHeight: 1.45, color: 'text.secondary' }}>
                  {dayType.detail}
                </Typography>
              ) : null}
            </Box>
            {selectedHoliday ? (
              <Box sx={{ flexShrink: 0 }}>
                <StatusChip tone="neutral" label={scopeLabel(selectedHoliday.scope)} />
              </Box>
            ) : null}
          </Paper>
        ) : null}

        {showTimeclockRequests && hasDayTimeclockActivity ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
            {correctionsOnDay.length > 0 ? (
              <Paper
                component="section"
                variant="outlined"
                aria-labelledby="hc-aside-corrections"
                sx={{ p: 1.75, border: 1, borderColor: 'divider' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.25,
                    mb: 1.25,
                  }}
                >
                  <Typography
                    component="h3"
                    id="hc-aside-corrections"
                    sx={{ m: 0, ...KICKER_SX }}
                  >
                    Correções de hora
                  </Typography>
                  <StatusChip tone="neutral" label={correctionsOnDay.length} />
                </Box>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {correctionsOnDay.map((c) => (
                    <Box
                      component="li"
                      key={c.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
                        <StatusChip
                          tone="info"
                          label={
                            c.applyMode === 'add_extra'
                              ? `${formatTimeclockEntryKind(c.kind)} extra`
                              : formatTimeclockEntryKind(c.kind)
                          }
                        />
                        <Typography component="strong" sx={{ fontSize: '0.9rem', fontWeight: 700 }}>
                          {c.requestedTime}
                        </Typography>
                        <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.35, color: 'text.secondary' }}>
                          Horário solicitado
                        </Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <StatusChip
                          tone={statusChipTone(c.status)}
                          label={statusCorrectionPt[c.status] || c.status}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ) : null}

            {invoicesOnDay.length > 0 ? (
              <Paper
                component="section"
                variant="outlined"
                aria-labelledby="hc-aside-invoices"
                sx={{ p: 1.75, border: 1, borderColor: 'divider' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.25,
                    mb: 1.25,
                  }}
                >
                  <Typography
                    component="h3"
                    id="hc-aside-invoices"
                    sx={{ m: 0, ...KICKER_SX }}
                  >
                    Notas fiscais
                  </Typography>
                  <StatusChip tone="neutral" label={invoicesOnDay.length} />
                </Box>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {invoicesOnDay.map((inv) => (
                    <Box
                      component="li"
                      key={inv.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
                        <Typography component="strong" sx={{ fontSize: '0.9rem', fontWeight: 700 }}>
                          Competência{' '}
                          {String(inv.referenceMonth).padStart(2, '0')}/{inv.referenceYear}
                        </Typography>
                        <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.35, color: 'text.secondary' }}>
                          {inv.invoiceNumber?.trim()
                            ? `NF ${inv.invoiceNumber.trim()}`
                            : 'Envio registrado'}
                        </Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <StatusChip
                          tone={statusChipTone(inv.status)}
                          label={statusInvoicePt[inv.status] || inv.status}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {showTimeclockRequests ? (
        <Button
          component={Link}
          to="/ponto-eletronico"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ flexShrink: 0, mt: 1.5, height: 44 }}
        >
          Abrir ponto eletrônico
        </Button>
      ) : null}
    </Box>
  ) : null;

  if (pickerOnly) {
    return (
      <Box className={className} sx={{ opacity: loading ? 0.85 : 1 }}>
        {pickerBlock}
        <Box
          role="status"
          sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', minHeight: 52 }}
        >
          <Typography component="strong" sx={{ display: 'block', fontSize: '0.88rem', mb: 0.5, textTransform: 'capitalize' }}>
            {formatDayLong(selected)}
          </Typography>
          {selectedHoliday ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <StatusChip tone="neutral" label={scopeLabel(selectedHoliday.scope)} />
              <Typography component="span" sx={{ fontSize: '0.8rem', lineHeight: 1.45 }}>
                {selectedHoliday.name}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ m: 0, fontSize: '0.8rem', color: 'text.secondary' }}>
              {dayType.title}
              {dayType.detail ? ` · ${dayType.detail}` : ''}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Paper
      className={className}
      variant={variant === 'hero' ? 'outlined' : 'elevation'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 0 : 1.5,
        p: compact ? 0 : { xs: 2, sm: variant === 'surface' ? 2.5 : 2 },
        border: compact ? 0 : 1,
        borderColor: 'divider',
        bgcolor: compact ? 'transparent' : 'background.paper',
        color: 'text.primary',
        fontSize: '0.875rem',
        opacity: loading ? 0.85 : 1,
      }}
    >
      {!compact ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography component="span" sx={KICKER_SX}>
            Calendário
          </Typography>
          <Typography sx={{ m: 0, fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>
            Feriados · Ilhéus, Bahia e Brasil
          </Typography>
        </Box>
      ) : null}

      {compact ? (
        <Grid container spacing={{ xs: 2, md: 0 }} sx={{ alignItems: 'stretch' }}>
          <Grid
            size={{ xs: 12, md: 5 }}
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              pr: { md: 2.5 },
              pb: { xs: 2, md: 0 },
              borderRight: { md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: { xs: 'divider', md: 'divider' },
            }}
          >
            <Typography component="span" sx={{ ...KICKER_SX, mb: 0.75 }}>
              Calendário
            </Typography>
            {pickerBlock}
          </Grid>
          <Grid
            size={{ xs: 12, md: 7 }}
            sx={{ minWidth: 0, pl: { md: 2.5 } }}
          >
            {asideBlock}
          </Grid>
        </Grid>
      ) : (
        <>
          {pickerBlock}

          <Box
            aria-hidden
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px 16px',
              fontSize: '0.72rem',
              color: 'text.secondary',
            }}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component="i"
                sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '999px', bgcolor: 'primary.main' }}
              />{' '}
              Feriado
            </Box>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component="i"
                sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '999px', bgcolor: 'text.secondary' }}
              />{' '}
              Fim de semana
            </Box>
          </Box>

          <Box
            role="status"
            sx={{
              p: 1.5,
              bgcolor: 'action.hover',
              minHeight: 52,
            }}
          >
            <Typography component="strong" sx={{ display: 'block', fontSize: '0.88rem', mb: 0.5 }}>
              {formatDayLong(selected)}
            </Typography>
            {selectedHoliday ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <StatusChip tone="neutral" label={scopeLabel(selectedHoliday.scope)} />
                <Typography component="span" sx={{ fontSize: '0.8rem', lineHeight: 1.45 }}>
                  {selectedHoliday.name}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ m: 0, fontSize: '0.8rem', color: 'text.secondary' }}>
                {dayType.title}
              </Typography>
            )}
          </Box>

          {holidays.length > 0 ? (
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: 0,
                p: 0,
                maxHeight: 120,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {holidays.map((h) => (
                <Box component="li" key={h.workDate}>
                  <Tooltip title={h.name}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => {
                        const d = workDateToLocalDate(h.workDate.slice(0, 10));
                        chooseDay(d);
                        setDisplayMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'flex-start',
                        gap: 1.25,
                        px: 1,
                        py: 0.75,
                        textAlign: 'left',
                        color: 'text.primary',
                        fontWeight: 400,
                      }}
                    >
                      <Box
                        component="time"
                        dateTime={h.workDate.slice(0, 10)}
                        sx={{
                          flexShrink: 0,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatDayShort(workDateToLocalDate(h.workDate.slice(0, 10)))}
                      </Box>
                      <Box component="span" sx={{ fontSize: '0.78rem', lineHeight: 1.35 }}>
                        {h.name}
                      </Box>
                    </Button>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          ) : null}
        </>
      )}
    </Paper>
  );
};

export default HolidayCalendar;
